/**
 * Event Builder — HTTP layer.
 *
 * Same endpoints, same verbs and same bodies as the four old wizard pages (plan 3.1).
 * Calls go through `axios-instance` directly (not through the redux actions) so that
 * every error path reports (plan 8.3: `saveEventType` swallows two error shapes today).
 *
 * Response unwrapping: the API wraps every payload as `{ success, code, message, data }`,
 * so `res.data.data` is the payload the old actions hand to `onSuccess`.
 */

import axios from "../../../../axios-instance";
import { getAdminBusinessId } from "../../../../utils/authHelper";
import { objectToFormData } from "../../../../utils/utility";
import { parseHasPaymentIntegration } from "../../../../utils/paymentIntegration";

const unwrap = (res) => ((res || {}).data || {}).data;

// ---- event pages -----------------------------------------------------------------------------------

export const getTypePage = (id) => axios.get(`/admin/event/event-type-page/${id}`).then(unwrap);
export const getDetailsPage = (id, isPaid) =>
  axios.get(`/admin/event/${isPaid ? "paid-event" : "event"}-details-page/${id}`).then(unwrap);
export const getAvailabilityPage = (id) => axios.get(`/admin/event/event-availability-page/${id}`).then(unwrap);
export const getAdvancedPage = (id) => axios.get(`/admin/event/event-advanced-page/${id}`).then(unwrap);

/** → event id (`{data:{id}}` payload, as `saveEventType` reads it) */
export const saveTypePage = (payload) =>
  axios.post("admin/event/save-event-type-page", payload).then((res) => {
    const body = unwrap(res) || {};
    return parseInt((body.data || {}).id, 10) || null;
  });

/** FormData, exactly like `saveEventDetails` (3.4 rule 9) */
export const saveDetailsPage = (payload) =>
  axios.post("admin/event/save-event-details-page", objectToFormData(payload)).then(unwrap);

export const saveAvailabilityPage = (payload) => axios.post("admin/event/save-event-availability-page", payload).then(unwrap);
export const saveAdvancedPage = (payload) => axios.post("admin/event/save-event-advanced-page", payload).then(unwrap);

// ---- lookups ------------------------------------------------------------------------------------------

export const getVenueList = () => axios.get("/venues").then((res) => (unwrap(res) || {}).venues || []);
export const getTimezoneList = () => axios.get("/timezones").then((res) => (unwrap(res) || {}).timezones || []);

/**
 * MainIntegrationController@getConnectedVenues returns `respond(['data' => $dataSet])`, i.e. the list
 * sits one level deeper than the other endpoints (`res.data.data.data`) — the old type page reads
 * `response.data` after sendGetRequest's own unwrap. Accept both shapes.
 */
export const getConnectedVenues = () =>
  axios.get(`/admin/connected-venues/${getAdminBusinessId()}`).then((res) => {
    const body = unwrap(res);
    if (Array.isArray(body)) return body;
    return Array.isArray((body || {}).data) ? body.data : [];
  });

/** Only call when feature 1 (CALENDAR_INTEGRATIONS) is enabled — the endpoint 403s otherwise. */
export const getConnectedCalendars = () =>
  axios.get(`admin/get-connected-calendars/${getAdminBusinessId()}`).then((res) => ((res || {}).data || {}).entity || []);

export const getScheduleTemplateList = () =>
  axios.get(`/admin/schedule/get-schedule-templates/${getAdminBusinessId()}`).then((res) => (unwrap(res) || {}).templates || []);

/**
 * ScheduleController@getScheduleDetails also returns `respond(['data' => $schedulesData])`; the old
 * Event-Availbility-Component reads `response.data.schedule_details`. Accept both shapes.
 */
export const getScheduleById = (scheduleId) =>
  axios.get(`/admin/schedule/get-schedule/${getAdminBusinessId()}/${scheduleId}`).then((res) => {
    const body = unwrap(res) || {};
    return body.schedule_details ? body : body.data || body;
  });

export const saveScheduleTemplate = (payload) => axios.post("admin/schedule/save-as-new-schedule-template", payload);

/** POST admin/staffs/{businessId} with the same body the old availability page sends. */
export const getStaffList = () =>
  axios
    .post(`/admin/staffs/${getAdminBusinessId()}`, { pageSize: 1000, page: 1, sortField: "id", sortOrder: "asc" })
    .then((res) => ((res || {}).data || {}).entity || []);

export const getEventConfig = () =>
  axios.get(`/admin/settings/event/${getAdminBusinessId()}`).then((res) => {
    const body = (res || {}).data || {};
    return (body.data && body.data.data) || body.data || null;
  });

/** Returns the user's timezone_id stored in their profile (used to seed new events). */
export const getUserTimezoneId = () =>
  axios.get("user-details").then((res) => {
    const profile = (res && res.data && res.data.data) || (res && res.data) || null;
    const id = profile && (profile.timezone_id || profile.data && profile.data.timezone_id);
    return id ? String(id) : "";
  });

export const getPaymentIntegrationStatus = () =>
  axios.get(`admin/payment-integration-status/${getAdminBusinessId()}`).then((res) => parseHasPaymentIntegration(res.data));

// ---- error helpers ---------------------------------------------------------------------------------------

export const errorStatus = (error) => (error && error.response ? error.response.status : 0);
export const errorBody = (error) => (error && error.response ? error.response.data || {} : {});
export const isFatal403 = (error) => errorStatus(error) === 403;
